import { useState, useMemo } from "react";
import { API, apiError, formatINR, daysUntil } from "../lib/api";
import GlobalSearch from "../components/GlobalSearch";
import { Trash2, Plus, Minus, Printer, Download, Receipt, X, Repeat, FlaskConical } from "lucide-react";
import { toast } from "sonner";

export default function Sales() {
  const [cart, setCart] = useState([]); // {medicine, qty}
  const [discountValue, setDiscountValue] = useState(0);
  const [discountType, setDiscountType] = useState("amount"); // 'amount' | 'percent'
  const [gstRate, setGstRate] = useState(0);
  const [payment, setPayment] = useState("Cash");
  const [paidAmount, setPaidAmount] = useState("");
  const [customer, setCustomer] = useState("");
  const [phone, setPhone] = useState("");
  const [notes, setNotes] = useState("");
  const [lastSale, setLastSale] = useState(null);
  const [loading, setLoading] = useState(false);
  const [equivPanel, setEquivPanel] = useState(null); // { source, equivalents, strict }

  const isCredit = payment === "Credit" || payment === "Partial";

  const fetchEquivalents = async (med, strict = true) => {
    try {
      const { data } = await API.get(`/medicines/${med.id}/equivalents`, {
        params: { strict, in_stock_only: true },
      });
      if ((data.equivalents || []).length > 0) {
        setEquivPanel({ source: med, equivalents: data.equivalents, strict });
        return true;
      }
      if (strict) {
        // fall back to same-salt match
        return fetchEquivalents(med, false);
      }
      setEquivPanel({ source: med, equivalents: [], strict: false });
      return false;
    } catch {
      setEquivPanel(null);
      return false;
    }
  };

  const addToCart = (med) => {
    if (!med) return;
    const today = new Date().toISOString().slice(0, 10);
    if (med.expiry_date < today) {
      toast.error(`${med.name} is expired`);
      fetchEquivalents(med);
      return;
    }
    if (med.quantity <= 0) {
      toast.error(`${med.name} is out of stock`);
      fetchEquivalents(med);
      return;
    }
    setEquivPanel(null);
    setCart((prev) => {
      const idx = prev.findIndex((x) => x.medicine.id === med.id);
      if (idx >= 0) {
        const next = [...prev];
        if (next[idx].qty + 1 > med.quantity) { toast.error(`Only ${med.quantity} available`); return prev; }
        next[idx] = { ...next[idx], qty: next[idx].qty + 1 };
        return next;
      }
      return [...prev, { medicine: med, qty: 1 }];
    });
  };

  const setQty = (id, delta) => {
    setCart((prev) => prev.map((x) => {
      if (x.medicine.id !== id) return x;
      const q = Math.max(1, x.qty + delta);
      if (q > x.medicine.quantity) { toast.error(`Only ${x.medicine.quantity} available`); return x; }
      return { ...x, qty: q };
    }));
  };

  const remove = (id) => setCart((prev) => prev.filter((x) => x.medicine.id !== id));

  const totals = useMemo(() => {
    const subtotal = cart.reduce((s, x) => s + x.medicine.mrp * x.qty, 0);
    const totalCost = cart.reduce((s, x) => s + (parseFloat(x.medicine.purchase_price) || 0) * x.qty, 0);
    const dv = parseFloat(discountValue) || 0;
    const discountAmt = discountType === "percent"
      ? Math.round(subtotal * dv) / 100
      : dv;
    // MRP is GST-inclusive — extract GST from the bill rather than add on top.
    const grossAfterDiscount = Math.max(subtotal - discountAmt, 0);
    const rate = parseFloat(gstRate) || 0;
    const gstAmt = rate > 0 ? (grossAfterDiscount * rate) / (100 + rate) : 0;
    const taxable = grossAfterDiscount - gstAmt;
    const total = grossAfterDiscount; // never exceeds sum(MRP × qty)
    const netRevenue = taxable; // what the store keeps ex-GST
    const estProfit = Math.max(netRevenue - totalCost, -totalCost);
    return { subtotal, discountAmt, taxable, gstAmt, total, totalCost, estProfit };
  }, [cart, discountValue, discountType, gstRate]);

  const checkout = async () => {
    if (cart.length === 0) { toast.error("Cart is empty"); return; }
    if (isCredit && (!customer.trim() || !phone.trim())) {
      toast.error("Customer name & phone are required for credit sales");
      return;
    }
    if (payment === "Partial") {
      const p = parseFloat(paidAmount);
      if (!p || p <= 0 || p >= totals.total) { toast.error("Partial payment must be > 0 and less than total"); return; }
    }
    setLoading(true);
    try {
      const body = {
        items: cart.map((c) => ({ medicine_id: c.medicine.id, quantity: c.qty })),
        discount: parseFloat(totals.discountAmt.toFixed(2)) || 0,
        gst_rate: parseFloat(gstRate) || 0,
        payment_mode: payment,
        customer_name: customer || "Walk-in",
        customer_phone: phone,
        notes,
      };
      if (payment === "Partial") body.paid_amount = parseFloat(paidAmount);
      const { data } = await API.post("/sales", body);
      const dueMsg = data.balance_due > 0 ? ` · ₹${data.balance_due.toFixed(2)} on credit` : "";
      toast.success(`Sale ${data.bill_number} recorded${dueMsg}`);
      setLastSale(data);
      setCart([]); setDiscountValue(0); setCustomer(""); setPhone(""); setPaidAmount(""); setNotes("");
      setPayment("Cash");
    } catch (e) { toast.error(apiError(e)); }
    finally { setLoading(false); }
  };

  const openInvoice = (id) => {
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/sales/${id}/invoice`;
    window.open(url, "_blank");
  };

  const printInvoice = (id) => {
    // Opens an HTML invoice that auto-triggers the browser print dialog
    window.open(`/invoice/${id}?auto=1`, "_blank", "width=900,height=1000");
  };

  return (
    <div className="p-4 sm:p-6 md:p-8 grid grid-cols-1 xl:grid-cols-[1fr_440px] gap-6" data-testid="sales-page">
      {/* LEFT: add items */}
      <div>
        <div className="mb-5">
          <div className="overline mb-2">Point of Sale</div>
          <h1 className="font-display text-3xl font-bold">New Sale</h1>
          <p className="text-sm text-slate-500 mt-1">Search or scan a barcode to add items to the cart.</p>
        </div>

        <div className="mb-5 bg-white border border-slate-200 rounded-xl p-4">
          <div className="overline text-[10px] mb-2">Add Item</div>
          <GlobalSearch mode="pick" onPick={addToCart} />
          <div className="mt-2 text-xs text-slate-500">Tip: barcode scanner works as keyboard — place cursor here and scan.</div>
        </div>

        {equivPanel && (
          <div className="mb-5 bg-amber-50 border border-amber-200 rounded-xl p-4" data-testid="pos-equivalents-panel">
            <div className="flex items-start justify-between gap-3 mb-3">
              <div className="flex items-start gap-3">
                <div className="w-9 h-9 rounded-lg bg-amber-100 text-amber-700 flex items-center justify-center shrink-0">
                  <Repeat className="w-5 h-5" />
                </div>
                <div>
                  <div className="font-semibold text-amber-900 text-sm">
                    {equivPanel.source.name} is {equivPanel.source.quantity <= 0 ? "out of stock" : "expired"}
                  </div>
                  {(equivPanel.source.composition || []).length > 0 ? (
                    <div className="text-xs text-amber-800 mt-0.5 flex items-center gap-1 flex-wrap">
                      <FlaskConical className="w-3 h-3" />
                      <span>Composition:</span>
                      {equivPanel.source.composition.map((c, i) => (
                        <span key={i} className="font-medium">
                          {c.salt}{c.strength != null ? ` ${c.strength}${c.unit || ""}` : ""}{i < equivPanel.source.composition.length - 1 ? " +" : ""}
                        </span>
                      ))}
                    </div>
                  ) : (
                    <div className="text-xs text-amber-700 mt-0.5">No salt composition recorded for this medicine — add it from Stock for smarter suggestions.</div>
                  )}
                </div>
              </div>
              <button onClick={() => setEquivPanel(null)} className="p-1 text-amber-700 hover:text-amber-900" data-testid="close-equivalents" aria-label="Close">
                <X className="w-4 h-4" />
              </button>
            </div>
            {equivPanel.equivalents.length === 0 ? (
              <div className="text-xs text-amber-800 italic" data-testid="pos-equivalents-empty">
                No equivalent medicines in stock right now.
              </div>
            ) : (
              <div>
                <div className="text-xs text-amber-800 mb-2">
                  Showing {equivPanel.equivalents.length} {equivPanel.strict ? "exact (salt + strength)" : "same-salt"} substitute{equivPanel.equivalents.length === 1 ? "" : "s"} in stock:
                </div>
                <ul className="space-y-2">
                  {equivPanel.equivalents.map((e) => (
                    <li key={e.id} className="bg-white rounded-lg border border-amber-200 p-3 flex items-center gap-3" data-testid={`pos-equiv-${e.id}`}>
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold text-sm text-slate-900 truncate">{e.name}</div>
                        <div className="text-xs text-slate-500 truncate">
                          {e.company} · Batch {e.batch_number}
                          {(e.composition || []).length > 0 && (
                            <span> · {e.composition.map((c, i) => `${c.salt}${c.strength != null ? ` ${c.strength}${c.unit || ""}` : ""}`).join(" + ")}</span>
                          )}
                        </div>
                      </div>
                      <div className="text-right shrink-0">
                        <div className="font-semibold text-sm">{formatINR(e.mrp)}</div>
                        <div className="text-xs"><span className={`tag ${e.quantity <= 10 ? "tag-warning" : "tag-success"}`}>{e.quantity} in stock</span></div>
                      </div>
                      <button onClick={() => addToCart(e)} className="btn-primary text-xs" data-testid={`pos-add-equiv-${e.id}`}>
                        <Plus className="w-3 h-3" /> Add
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

        <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
          <div className="p-4 border-b border-slate-200 flex items-center justify-between">
            <h3 className="font-display font-semibold">Cart <span className="text-slate-400 font-normal text-sm">({cart.length} items)</span></h3>
            {cart.length > 0 && (
              <button onClick={() => setCart([])} className="text-xs text-slate-500 hover:text-red-600" data-testid="clear-cart">Clear</button>
            )}
          </div>
          {cart.length === 0 ? (
            <div className="p-12 text-center">
              <Receipt className="w-10 h-10 text-slate-300 mx-auto mb-3" />
              <div className="text-sm text-slate-500">Cart is empty. Start by searching a medicine above.</div>
            </div>
          ) : (
            <ul className="divide-y divide-slate-100">
              {cart.map((item) => {
                const d = daysUntil(item.medicine.expiry_date);
                const cost = parseFloat(item.medicine.purchase_price) || 0;
                const mrp = parseFloat(item.medicine.mrp) || 0;
                const margin = mrp > 0 ? ((mrp - cost) / mrp) * 100 : 0;
                return (
                  <li key={item.medicine.id} className="p-4 flex items-center gap-3" data-testid={`cart-${item.medicine.id}`}>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-slate-900 truncate">{item.medicine.name}</div>
                      <div className="text-xs text-slate-500 truncate">
                        {item.medicine.company} · Batch {item.medicine.batch_number} · {formatINR(item.medicine.mrp)}/unit
                        {d !== null && d <= 90 && <span className={`ml-2 tag ${d < 30 ? "tag-danger" : "tag-warning"}`}>Exp {d}d</span>}
                      </div>
                      {cost > 0 && (
                        <div className="text-[11px] text-slate-500 mt-1 flex items-center gap-2" data-testid={`cost-${item.medicine.id}`}>
                          <span className="font-mono">Cost {formatINR(cost)}</span>
                          <span className={`tag ${margin >= 20 ? "tag-success" : margin >= 10 ? "tag-warning" : "tag-danger"} !text-[10px] !px-1.5 !py-0`}>
                            {margin.toFixed(1)}% margin
                          </span>
                          <span className="text-slate-400">·</span>
                          <span className="text-slate-400">Profit {formatINR((mrp - cost) * item.qty)}</span>
                        </div>
                      )}
                    </div>
                    <div className="flex items-center gap-1 bg-slate-100 rounded-lg p-0.5">
                      <button onClick={() => setQty(item.medicine.id, -1)} className="p-1 hover:bg-white rounded"><Minus className="w-3 h-3" /></button>
                      <span className="font-mono text-sm font-semibold w-8 text-center">{item.qty}</span>
                      <button onClick={() => setQty(item.medicine.id, 1)} className="p-1 hover:bg-white rounded"><Plus className="w-3 h-3" /></button>
                    </div>
                    <div className="text-right w-24 font-semibold">{formatINR(item.medicine.mrp * item.qty)}</div>
                    <button onClick={() => remove(item.medicine.id)} className="p-1.5 text-slate-400 hover:text-red-600" data-testid={`remove-${item.medicine.id}`}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>

      {/* RIGHT: bill summary */}
      <aside className="xl:sticky xl:top-20 h-fit space-y-4">
        <div className="bg-white border border-slate-200 rounded-xl p-5">
          <div className="overline mb-3">Customer {isCredit && <span className="text-red-600 font-semibold ml-1">· REQUIRED for credit</span>}</div>
          <div className="grid grid-cols-2 gap-2">
            <input placeholder={isCredit ? "Name *" : "Name (optional)"} value={customer} onChange={(e) => setCustomer(e.target.value)}
                   data-testid="customer-name-input" required={isCredit}
                   className={`input-row col-span-2 ${isCredit && !customer ? "ring-2 ring-red-300 border-red-300" : ""}`} />
            <input placeholder={isCredit ? "Phone *" : "Phone"} value={phone} onChange={(e) => setPhone(e.target.value)}
                   data-testid="customer-phone-input" required={isCredit}
                   className={`input-row col-span-2 ${isCredit && !phone ? "ring-2 ring-red-300 border-red-300" : ""}`} />
            <input placeholder="Notes (optional)" value={notes} onChange={(e) => setNotes(e.target.value)}
                   className="input-row col-span-2" />
          </div>
          {isCredit && (
            <div className="mt-3 text-xs bg-amber-50 border border-amber-200 rounded-md p-2.5 text-amber-800">
              <b>Credit sale</b> — a customer ledger will be auto-created/updated with this transaction.
              {customer && phone && <> Existing customer with phone <b className="font-mono">{phone}</b> will be matched if found.</>}
            </div>
          )}
        </div>

        <div className="bg-white border border-slate-200 rounded-xl p-5 space-y-3">
          <div className="overline">Bill Summary</div>
          <Line label="Subtotal (incl. GST)" value={formatINR(totals.subtotal)} />
          {totals.totalCost > 0 && (
            <div className="flex justify-between text-xs text-slate-500" data-testid="bill-cost-line">
              <span>Cost of goods</span>
              <span className="font-mono">{formatINR(totals.totalCost)}</span>
            </div>
          )}
          <div className="space-y-1.5">
            <div className="flex items-center justify-between gap-2 text-sm">
              <span className="text-slate-500">Discount</span>
              <div className="flex items-center gap-2">
                <div className="flex bg-slate-100 rounded-md p-0.5" data-testid="discount-toggle">
                  <button type="button" onClick={() => setDiscountType("amount")}
                          data-testid="discount-mode-amount"
                          className={`px-2 py-1 text-xs font-semibold rounded ${discountType === "amount" ? "bg-white text-[#002FA7] shadow-sm" : "text-slate-500"}`}>₹</button>
                  <button type="button" onClick={() => setDiscountType("percent")}
                          data-testid="discount-mode-percent"
                          className={`px-2 py-1 text-xs font-semibold rounded ${discountType === "percent" ? "bg-white text-[#002FA7] shadow-sm" : "text-slate-500"}`}>%</button>
                </div>
                <input
                  type="number"
                  min="0"
                  max={discountType === "percent" ? 100 : undefined}
                  step="0.01"
                  value={discountValue}
                  onChange={(e) => setDiscountValue(e.target.value)}
                  data-testid="discount-input"
                  className="w-24 text-right border border-slate-200 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]/20 focus:border-[#002FA7]"
                />
              </div>
            </div>
            {discountType === "percent" && parseFloat(discountValue) > 0 && (
              <div className="flex justify-between text-xs text-slate-500 pl-1">
                <span>{parseFloat(discountValue) || 0}% of subtotal</span>
                <span className="font-medium text-slate-700">− {formatINR(totals.discountAmt)}</span>
              </div>
            )}
            {discountType === "amount" && (
              <div className="flex justify-end text-xs text-slate-400 pl-1">
                <span>= {totals.subtotal > 0 ? ((totals.discountAmt / totals.subtotal) * 100).toFixed(1) : "0.0"}% off</span>
              </div>
            )}
          </div>
          <div className="flex items-center justify-between gap-2 text-sm">
            <span className="text-slate-500">GST (%) <span className="text-[10px] text-slate-400">— extracted from MRP</span></span>
            <input type="number" value={gstRate} onChange={(e) => setGstRate(e.target.value)}
                   data-testid="gst-input"
                   className="w-28 text-right border border-slate-200 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]/20 focus:border-[#002FA7]" />
          </div>
          {parseFloat(gstRate) > 0 && (
            <Line label={`Taxable value (ex-GST ${gstRate}%)`} value={formatINR(totals.taxable)} />
          )}
          <Line label="GST Amount" value={formatINR(totals.gstAmt)} />
          <div className="border-t border-slate-200 pt-3 flex items-center justify-between">
            <span className="font-display font-semibold text-lg">Grand Total</span>
            <span className="font-display font-bold text-2xl text-[#002FA7]" data-testid="grand-total">{formatINR(totals.total)}</span>
          </div>
          {totals.totalCost > 0 && cart.length > 0 && (
            <div className="flex justify-between text-xs rounded-md px-2.5 py-1.5 border bg-emerald-50 border-emerald-200 text-emerald-800" data-testid="bill-profit-line">
              <span>Est. profit (ex-GST)</span>
              <span className="font-semibold font-mono">{formatINR(totals.estProfit)}</span>
            </div>
          )}
          <div>
            <div className="overline mb-2 mt-3">Payment Mode</div>
            <div className="grid grid-cols-3 gap-1 bg-slate-100 p-1 rounded-lg">
              {["Cash", "UPI", "Card", "Mixed", "Credit", "Partial"].map((m) => (
                <button key={m} type="button" onClick={() => setPayment(m)}
                  data-testid={`pay-${m.toLowerCase()}`}
                  className={`py-2 text-xs font-semibold rounded-md transition-colors ${payment === m ? (m === "Credit" || m === "Partial" ? "bg-amber-500 text-white shadow-sm" : "bg-white text-[#002FA7] shadow-sm") : "text-slate-600"}`}>
                  {m}
                </button>
              ))}
            </div>
          </div>
          {payment === "Partial" && (
            <div className="mt-2 grid grid-cols-2 gap-2 items-center">
              <span className="text-sm text-slate-500">Amount paid now</span>
              <input type="number" step="0.01" min="0" value={paidAmount} onChange={(e) => setPaidAmount(e.target.value)}
                     data-testid="paid-amount-input"
                     className="text-right border border-slate-200 rounded px-2 py-1 text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7]/20 focus:border-[#002FA7]" placeholder="0.00" />
              {paidAmount && (
                <div className="col-span-2 flex justify-between text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2.5 py-1.5">
                  <span>On credit (added to customer ledger)</span>
                  <span className="font-semibold">{formatINR(Math.max(0, totals.total - (parseFloat(paidAmount) || 0)))}</span>
                </div>
              )}
            </div>
          )}
          {payment === "Credit" && totals.total > 0 && (
            <div className="mt-2 flex justify-between text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2.5 py-1.5">
              <span>Full amount on credit</span>
              <span className="font-semibold">{formatINR(totals.total)}</span>
            </div>
          )}
          <button onClick={checkout} disabled={cart.length === 0 || loading}
                  data-testid="checkout-btn"
                  className="w-full btn-primary justify-center py-3 mt-2">
            {loading ? "Processing..." : "Checkout & Print Bill"}
          </button>
        </div>
      </aside>

      {lastSale && (
        <div className="fixed inset-0 z-50 bg-slate-900/50 backdrop-blur-sm flex items-center justify-center p-4" data-testid="invoice-popup">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg overflow-hidden">
            <div className="p-5 border-b border-slate-200 flex items-center justify-between">
              <div>
                <div className="overline">Invoice generated</div>
                <h3 className="font-display text-xl font-semibold">{lastSale.bill_number}</h3>
              </div>
              <button onClick={() => setLastSale(null)} className="p-1 hover:bg-slate-100 rounded"><X className="w-4 h-4" /></button>
            </div>
            <div className="p-5 space-y-3">
              <div className="flex justify-between text-sm"><span className="text-slate-500">Date</span><span>{new Date(lastSale.created_at).toLocaleString("en-IN")}</span></div>
              <div className="flex justify-between text-sm"><span className="text-slate-500">Items</span><span>{lastSale.items.length}</span></div>
              <div className="flex justify-between text-sm"><span className="text-slate-500">Payment</span><span>{lastSale.payment_mode}</span></div>
              <div className="flex justify-between text-lg font-semibold bg-slate-50 p-3 rounded-lg"><span>Total</span><span className="text-[#002FA7]">{formatINR(lastSale.total)}</span></div>
            </div>
            <div className="p-5 border-t border-slate-200 flex gap-2">
              <button onClick={() => printInvoice(lastSale.id)} className="btn-primary flex-1 justify-center" data-testid="invoice-print">
                <Printer className="w-4 h-4" /> Print
              </button>
              <button onClick={() => openInvoice(lastSale.id)} className="btn-ghost flex-1 justify-center" data-testid="invoice-download">
                <Download className="w-4 h-4" /> Download PDF
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{`.input-row{border:1px solid #E2E8F0;border-radius:8px;padding:.5rem .75rem;font-size:.875rem;outline:none;width:100%}.input-row:focus{border-color:#002FA7;box-shadow:0 0 0 3px rgba(0,47,167,.15)}`}</style>
    </div>
  );
}

function Line({ label, value }) {
  return <div className="flex justify-between text-sm"><span className="text-slate-500">{label}</span><span className="font-medium">{value}</span></div>;
}
